import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  FormCard,
  FormCheckboxGroup,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  financeStructureApi,
  formatAgorot,
  shekelsToAgorot,
  type FinanceCategory,
} from "../../api/financeStructureApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { CashFileImportDialog } from "../cashLedger/CashFileImportDialog";

const PRESET_COLORS = [
  "#4caf50",
  "#2196f3",
  "#f44336",
  "#ff9800",
  "#9c27b0",
  "#00bcd4",
  "#795548",
  "#607d8b",
  "#e91e63",
  "#3f51b5",
];

type Props = { writeCustomerId: string };

export function CategoriesTab({ writeCustomerId }: Props) {
  const [rows, setRows] = useState<FinanceCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceCategory | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>("");
  const [deleteChildrenCount, setDeleteChildrenCount] = useState(0);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeIds, setMergeIds] = useState<string[]>([]);
  const [mergeName, setMergeName] = useState("");
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustCategory, setAdjustCategory] = useState<FinanceCategory | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [excludeFromBalance, setExcludeFromBalance] = useState(false);
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [monthlyExpense, setMonthlyExpense] = useState("");
  const [defaultExpense, setDefaultExpense] = useState("");
  const [defaultIncome, setDefaultIncome] = useState("");
  const [defaultSubscription, setDefaultSubscription] = useState("");
  const [recurringExpenseEnabled, setRecurringExpenseEnabled] = useState(false);
  const [recurringExpenseAmount, setRecurringExpenseAmount] = useState("");
  const [recurringExpenseBillingDay, setRecurringExpenseBillingDay] = useState("1");
  const [recurringIncomeEnabled, setRecurringIncomeEnabled] = useState(false);
  const [recurringIncomeAmount, setRecurringIncomeAmount] = useState("");
  const [recurringIncomeBillingDay, setRecurringIncomeBillingDay] = useState("1");

  const [adjustDir, setAdjustDir] = useState<"credit" | "debit">("credit");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNotes, setAdjustNotes] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cats = await financeStructureApi.listCategories();
      setRows(cats);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const roots = useMemo(() => rows.filter((r) => !r.parentId && !r.isSystem), [rows]);

  const mergeOptions = useMemo(() => {
    return rows
      .filter((r) => !r.isSystem)
      .map((r) => ({
        value: r.id,
        label: r.displayName ?? r.name,
      }));
  }, [rows]);

  const mergeSaveBlockedReason = useMemo(() => {
    if (mergeIds.length < 2) return "בחרו לפחות 2 קטגוריות";
    if (!mergeName.trim()) return "הזינו שם מאוחד";
    return null;
  }, [mergeIds.length, mergeName]);

  const openCreate = (parent?: string | null) => {
    setEditing(null);
    setName("");
    setDescription("");
    setColor(null);
    setParentId(parent ?? null);
    setIsDefault(false);
    setExcludeFromBalance(false);
    setMonthlyIncome("");
    setMonthlyExpense("");
    setDefaultExpense("");
    setDefaultIncome("");
    setDefaultSubscription("");
    setRecurringExpenseEnabled(false);
    setRecurringExpenseAmount("");
    setRecurringExpenseBillingDay("1");
    setRecurringIncomeEnabled(false);
    setRecurringIncomeAmount("");
    setRecurringIncomeBillingDay("1");
    setDialogOpen(true);
  };

  const openEdit = (row: FinanceCategory) => {
    setEditing(row);
    setName(row.name);
    setDescription(row.description ?? "");
    setColor(row.color);
    setParentId(row.parentId);
    setIsDefault(row.isDefault);
    setExcludeFromBalance(row.excludeFromBalance);
    setMonthlyIncome(
      row.monthlyIncomeTarget != null ? String(row.monthlyIncomeTarget / 100) : "",
    );
    setMonthlyExpense(
      row.monthlyExpenseBudget != null ? String(row.monthlyExpenseBudget / 100) : "",
    );
    setDefaultExpense(
      row.defaultExpenseAmountAgorot != null
        ? String(row.defaultExpenseAmountAgorot / 100)
        : "",
    );
    setDefaultIncome(
      row.defaultIncomeAmountAgorot != null
        ? String(row.defaultIncomeAmountAgorot / 100)
        : "",
    );
    setDefaultSubscription(
      row.defaultSubscriptionAmountAgorot != null
        ? String(row.defaultSubscriptionAmountAgorot / 100)
        : "",
    );
    setRecurringExpenseEnabled(row.recurringExpenseEnabled ?? false);
    setRecurringExpenseAmount(
      row.recurringExpenseAmountAgorot != null
        ? String(row.recurringExpenseAmountAgorot / 100)
        : "",
    );
    setRecurringExpenseBillingDay(
      row.recurringExpenseBillingDay != null
        ? String(row.recurringExpenseBillingDay)
        : "1",
    );
    setRecurringIncomeEnabled(row.recurringIncomeEnabled ?? false);
    setRecurringIncomeAmount(
      row.recurringIncomeAmountAgorot != null
        ? String(row.recurringIncomeAmountAgorot / 100)
        : "",
    );
    setRecurringIncomeBillingDay(
      row.recurringIncomeBillingDay != null
        ? String(row.recurringIncomeBillingDay)
        : "1",
    );
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        color,
        parentId,
        isDefault: parentId ? false : isDefault,
        excludeFromBalance,
        monthlyIncomeTarget: monthlyIncome
          ? shekelsToAgorot(Number(monthlyIncome))
          : null,
        monthlyExpenseBudget: monthlyExpense
          ? shekelsToAgorot(Number(monthlyExpense))
          : null,
        defaultExpenseAmountAgorot: defaultExpense
          ? shekelsToAgorot(Number(defaultExpense))
          : null,
        defaultIncomeAmountAgorot: defaultIncome
          ? shekelsToAgorot(Number(defaultIncome))
          : null,
        defaultSubscriptionAmountAgorot: defaultSubscription
          ? shekelsToAgorot(Number(defaultSubscription))
          : null,
        recurringExpenseEnabled,
        recurringExpenseAmountAgorot: recurringExpenseEnabled
          ? shekelsToAgorot(Number(recurringExpenseAmount))
          : null,
        recurringExpenseBillingDay: recurringExpenseEnabled
          ? Number(recurringExpenseBillingDay) || 1
          : null,
        recurringIncomeEnabled,
        recurringIncomeAmountAgorot: recurringIncomeEnabled
          ? shekelsToAgorot(Number(recurringIncomeAmount))
          : null,
        recurringIncomeBillingDay: recurringIncomeEnabled
          ? Number(recurringIncomeBillingDay) || 1
          : null,
      };
      if (editing) {
        await financeStructureApi.updateCategory(editing.id, body);
      } else {
        await financeStructureApi.createCategory(body);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "displayName",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 180,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as FinanceCategory;
          return (
            <span>
              {r.color ? (
                <span className="fs-color-swatch" style={{ background: r.color }} />
              ) : null}
              {r.displayName ?? r.name}
              {r.isDefault ? <span className="fs-chip fs-chip--default">ברירת מחדל</span> : null}
              {r.isSystem ? <span className="fs-chip fs-chip--system">מערכת</span> : null}
            </span>
          );
        },
      },
      {
        key: "subtreeUsageCount",
        title: "שימושים",
        sortable: true,
        minWidth: 80,
      },
      {
        key: "subtreeIncomeTotal",
        title: "הכנסות",
        sortable: true,
        minWidth: 100,
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.subtreeIncomeTotal ?? 0)),
      },
      {
        key: "subtreeExpenseTotal",
        title: "הוצאות",
        sortable: true,
        minWidth: 100,
        render: (_value: unknown, row: Record<string, unknown>) =>
          formatAgorot(Number(row.subtreeExpenseTotal ?? 0)),
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 360,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const r = row as unknown as FinanceCategory;
          if (r.isSystem) return null;
          return (
            <TableRowActions>
              <QuietButton type="button" onClick={() => openEdit(r)}>
                עריכה
              </QuietButton>
              {!r.parentId ? (
                <QuietButton type="button" onClick={() => openCreate(r.id)}>
                  תת-קטגוריה
                </QuietButton>
              ) : null}
              <QuietButton
                type="button"
                onClick={() => {
                  setAdjustCategory(r);
                  setAdjustAmount("");
                  setAdjustNotes("");
                  setAdjustDir("credit");
                  setAdjustOpen(true);
                }}
              >
                התאמה
              </QuietButton>
              {!r.isDefault && !r.parentId ? (
                <QuietButton
                  type="button"
                  onClick={() =>
                    void financeStructureApi
                      .setDefaultCategory(r.id)
                      .then(load)
                      .catch((e) => setError(toClientApiError(e).message))
                  }
                >
                  ברירת מחדל
                </QuietButton>
              ) : null}
              <QuietButton
                type="button"
                tone="danger"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteError(null);
                  setDeleteId(r.id);
                  setDeleteName(r.displayName ?? r.name);
                  setDeleteChildrenCount(Number(r.childrenCount ?? 0));
                }}
              >
                מחיקה
              </QuietButton>
            </TableRowActions>
          );
        },
      },
    ],
    [load],
  );

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="קטגוריה חדשה"
        onPrimary={() => openCreate(null)}
        secondary={
          <>
            <SettingsButton
              type="button"
              variant="settings"
              size="sm"
              onClick={() => setImportOpen(true)}
            >
              ייבוא מקובץ
            </SettingsButton>
            <SettingsButton
              type="button"
              variant="settings"
              size="sm"
              onClick={() => {
                setMergeIds([]);
                setMergeName("");
                setMergeError(null);
                setMergeOpen(true);
              }}
            >
              איחוד
            </SettingsButton>
          </>
        }
      />
      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p>טוען…</p> : null}
      {!loading ? (
        <div className="page-section__table-surface">
          <TableKit
            columns={columns}
            data={rows as unknown as Record<string, unknown>[]}
            clientSideMode
            emptyMessage="אין קטגוריות"
            exportFileName="categories"
            showExport
            showFilters
            showColumnPicker
          />
        </div>
      ) : null}

      <FormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="md">
        <FormCard
          title={editing ? "עריכת קטגוריה" : "קטגוריה חדשה"}
          onSave={() => {
            void save();
          }}
          onCancel={() => setDialogOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !name.trim()}
        >
          <FormSection title="פרטים">
            <FormGrid>
              <FormField
                label="שם"
                required
                value={name}
                onChange={setName}
                disabled={Boolean(editing?.isSystem)}
              />
              <FormField label="תיאור" value={description} onChange={setDescription} />
              <FormField
                label="קטגוריית אב"
                value={parentId ?? ""}
                onChange={(v) => setParentId(v || null)}
                disabled={Boolean(editing?.isSystem)}
                options={[
                  { value: "", label: "— ראשית —" },
                  ...roots
                    .filter((r) => r.id !== editing?.id)
                    .map((r) => ({ value: r.id, label: r.name })),
                ]}
              />
              <FormField label="צבע">
                <div className="fs-presets">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`fs-presets__swatch${color === c ? " fs-presets__swatch--selected" : ""}`}
                      style={{ background: c }}
                      onClick={() => setColor(c)}
                      aria-label={c}
                    />
                  ))}
                  <input
                    type="color"
                    className="formkit-input"
                    value={color ?? "#4f46e5"}
                    onChange={(e) => setColor(e.target.value)}
                    style={{ width: 44, minHeight: 32, padding: 2 }}
                  />
                  <QuietButton type="button" onClick={() => setColor(null)}>
                    נקה
                  </QuietButton>
                </div>
              </FormField>
              {!parentId ? (
                <FormField
                  checkbox
                  label="קטגוריית ברירת מחדל"
                  checked={isDefault}
                  onChange={
                    ((v: boolean) => setIsDefault(v)) as unknown as (
                      value: string,
                    ) => void
                  }
                />
              ) : null}
              <FormField
                checkbox
                label="לא נכלל במאזן"
                checked={excludeFromBalance}
                onChange={
                  ((v: boolean) => setExcludeFromBalance(v)) as unknown as (
                    value: string,
                  ) => void
                }
              />
              <FormField
                label="יעד הכנסה חודשי (₪)"
                type="number"
                value={monthlyIncome}
                onChange={setMonthlyIncome}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField
                label="תקציב הוצאה חודשי (₪)"
                type="number"
                value={monthlyExpense}
                onChange={setMonthlyExpense}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </FormGrid>
          </FormSection>

          <FormSection title="סכומי ברירת מחדל (₪)">
            <FormGrid>
              <FormField
                label="ברירת מחדל להוצאה"
                type="number"
                value={defaultExpense}
                onChange={setDefaultExpense}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField
                label="ברירת מחדל להכנסה"
                type="number"
                value={defaultIncome}
                onChange={setDefaultIncome}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField
                label="ברירת מחדל למנוי"
                type="number"
                value={defaultSubscription}
                onChange={setDefaultSubscription}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </FormGrid>
          </FormSection>

          <FormSection title="עלות קבועה מחזורית">
            <FormGrid>
              <FormField
                checkbox
                label="הפעל הוצאה חודשית קבועה"
                checked={recurringExpenseEnabled}
                onChange={
                  ((v: boolean) => setRecurringExpenseEnabled(v)) as unknown as (
                    value: string,
                  ) => void
                }
              />
              {recurringExpenseEnabled ? (
                <>
                  <FormField
                    label="סכום הוצאה (₪)"
                    required
                    type="number"
                    value={recurringExpenseAmount}
                    onChange={setRecurringExpenseAmount}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                  <FormField
                    label="יום בחודש (1–28)"
                    required
                    type="number"
                    value={recurringExpenseBillingDay}
                    onChange={setRecurringExpenseBillingDay}
                    inputProps={{ min: 1, max: 28 }}
                  />
                </>
              ) : null}
              <FormField
                checkbox
                label="הפעל הכנסה חודשית קבועה"
                checked={recurringIncomeEnabled}
                onChange={
                  ((v: boolean) => setRecurringIncomeEnabled(v)) as unknown as (
                    value: string,
                  ) => void
                }
              />
              {recurringIncomeEnabled ? (
                <>
                  <FormField
                    label="סכום הכנסה (₪)"
                    required
                    type="number"
                    value={recurringIncomeAmount}
                    onChange={setRecurringIncomeAmount}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                  <FormField
                    label="יום בחודש (1–28)"
                    required
                    type="number"
                    value={recurringIncomeBillingDay}
                    onChange={setRecurringIncomeBillingDay}
                    inputProps={{ min: 1, max: 28 }}
                  />
                </>
              ) : null}
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <FormDialog
        open={mergeOpen}
        onClose={() => {
          if (!saving) {
            setMergeOpen(false);
            setMergeError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title="איחוד קטגוריות"
          error={mergeError}
          onSave={() => {
            void (async () => {
              setSaving(true);
              setMergeError(null);
              try {
                if (mergeIds.length < 2) {
                  setMergeError("בחרו לפחות 2 קטגוריות");
                  return;
                }
                if (!mergeName.trim()) {
                  setMergeError("הזינו שם מאוחד");
                  return;
                }
                await financeStructureApi.mergeCategories({
                  categoryIds: mergeIds,
                  targetName: mergeName.trim(),
                });
                setMergeOpen(false);
                setMergeIds([]);
                setMergeName("");
                await load();
              } catch (err) {
                setMergeError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => {
            setMergeOpen(false);
            setMergeError(null);
          }}
          saveLabel="איחוד"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || Boolean(mergeSaveBlockedReason)}
        >
          <FormSection title="בחירה">
            <FormField
              label="שם מאוחד"
              required
              value={mergeName}
              onChange={setMergeName}
            />
            <FormCheckboxGroup
              label="קטגוריות למיזוג"
              value={mergeIds}
              onChange={(next) => {
                setMergeError(null);
                setMergeIds(next);
              }}
              options={mergeOptions}
            />
            {mergeSaveBlockedReason ? (
              <p className="form-hint" style={{ marginTop: 8 }}>
                {mergeSaveBlockedReason}
              </p>
            ) : null}
          </FormSection>
        </FormCard>
      </FormDialog>

      <FormDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} size="md">
        <FormCard
          title={`התאמה ידנית — ${adjustCategory?.name ?? ""}`}
          onSave={() => {
            void (async () => {
              if (!adjustCategory) return;
              setSaving(true);
              try {
                await financeStructureApi.manualAdjust({
                  customerId: writeCustomerId,
                  categoryId: adjustCategory.id,
                  direction: adjustDir,
                  amountAgorot: shekelsToAgorot(Number(adjustAmount)),
                  notes: adjustNotes.trim() || null,
                });
                setAdjustOpen(false);
                await load();
              } catch (err) {
                setError(toClientApiError(err).message);
              } finally {
                setSaving(false);
              }
            })();
          }}
          onCancel={() => setAdjustOpen(false)}
          saveLabel="ביצוע"
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={saving || !adjustAmount || Number(adjustAmount) <= 0}
        >
          <FormSection title="התאמה">
            <FormGrid>
              <FormField
                label="כיוון"
                value={adjustDir}
                onChange={(v) => setAdjustDir(v as "credit" | "debit")}
                options={[
                  { value: "credit", label: "זכות (הכנסה)" },
                  { value: "debit", label: "חובה (הוצאה)" },
                ]}
              />
              <FormField
                label="סכום (₪)"
                required
                type="number"
                value={adjustAmount}
                onChange={setAdjustAmount}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <FormField label="הערה" value={adjustNotes} onChange={setAdjustNotes} />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleteId)}
        onClose={() => {
          if (!deleting) {
            setDeleteId(null);
            setDeleteError(null);
            setDeleteChildrenCount(0);
          }
        }}
        title="מחיקת קטגוריה"
        message={
          deleteError
            ? deleteError
            : deleteChildrenCount > 0
              ? `למחוק את "${deleteName || "הקטגוריה"}"? תתי־הקטגוריות יהפכו לראשיות, והרשומות המקושרות יועברו לקטגוריית המערכת.`
              : `למחוק את "${deleteName || "הקטגוריה"}"? הרשומות המקושרות יועברו לקטגוריית המערכת.`
        }
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={deleting}
        onCancel={() => {
          if (!deleting) {
            setDeleteId(null);
            setDeleteError(null);
            setDeleteChildrenCount(0);
          }
        }}
        onConfirm={() => {
          if (!deleteId || deleting) return;
          setDeleting(true);
          setDeleteError(null);
          setError(null);
          void financeStructureApi
            .deleteCategory(deleteId)
            .then(() => {
              setDeleteId(null);
              setDeleteName("");
              setDeleteChildrenCount(0);
              return load();
            })
            .catch((e) => {
              const msg = toClientApiError(e).message;
              setDeleteError(msg);
              setError(msg);
            })
            .finally(() => setDeleting(false));
        }}
      />

      <CashFileImportDialog<
        Awaited<ReturnType<typeof financeStructureApi.previewCategoriesImportFile>>
      >
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="ייבוא קטגוריות מקובץ"
        hint="עמודות: שם/name, קטגוריית אב/parent, תיאור, צבע (#RRGGBB), סדר"
        sampleColumns={[
          { key: "name", label: "שם" },
          { key: "parentName", label: "אב" },
          { key: "description", label: "תיאור" },
        ]}
        onPreview={(file) => financeStructureApi.previewCategoriesImportFile(file)}
        onCommit={async (preview) => {
          const result = await financeStructureApi.importCategories(preview.rows);
          return { created: result.created };
        }}
        onDone={() => void load()}
      />
    </div>
  );
}
